import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, TextField, Alert } from '@mui/material'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import FixTaskModal from '../../../commonUnit/FixTaskModal'
import { getCaretCoordinates } from '../caretCoordinates'

// How long the caret has to sit still in the description before the inline
// "Press Tab for AI support" hint appears next to it.
const IDLE_HINT_DELAY_MS = 1500
// Rough rendered width of the hint, used only to decide whether it fits after the caret
// on the current line or has to drop to the start of the next one.
const HINT_WIDTH_ESTIMATE_PX = 200

// Step 1. Problem name + problem description, with the same AI rewrite assistant
// AddSessionDialog already offers - reachable from the "Refine with AI" button or, once the
// caret has been idle for a few seconds, by pressing Tab.
//
// session.task is stored and rendered as plain pre-wrap text everywhere it is consumed,
// and the repo has no rich-text editor dependency (react-markdown is in package.json but
// imported nowhere), so this is a multiline TextField rather than the WYSIWYG toolbar in
// the reference design. Adding an editor here would mean every existing reader - TaskCard,
// the student IDE, the AI tutor prompt - starts receiving HTML it does not render.
const DetailsStep = ({ subject, setSubject, task, setTask, typeMeta }) => {
  // FixTaskModal regenerates on every taskContent change (FixTaskModal.jsx:22-25), so it
  // gets its own buffer rather than the live task state - otherwise accepting a rewrite
  // immediately retriggers generation. Same pattern as AddSessionDialog.jsx:50-54.
  const [generateDescription, setGenerateDescription] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)

  const descriptionRef = useRef(null)
  const fieldWrapRef = useRef(null)
  const idleTimerRef = useRef(null)
  // { top, left } in px relative to fieldWrapRef, or null when hidden.
  const [hint, setHint] = useState(null)

  useEffect(() => {
    if (!isModalOpen && generateDescription !== '') {
      setTask(generateDescription)
    }
  }, [generateDescription, isModalOpen]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleAISuggestion = () => {
    setGenerateDescription(task)
    setIsModalOpen(true)
  }

  const clearIdleTimer = () => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }

  // Works out where the hint goes: just after the caret on its own line, or at the start
  // of the next line when it would overflow. Returns null when the caret is scrolled out
  // of the visible part of the textarea, so the hint never floats over unrelated text.
  const computeHintPosition = useCallback(() => {
    const textarea = descriptionRef.current
    const wrap = fieldWrapRef.current
    if (!textarea || !wrap) return null

    const caret = getCaretCoordinates(textarea, textarea.selectionEnd)
    const areaRect = textarea.getBoundingClientRect()
    const wrapRect = wrap.getBoundingClientRect()
    const originTop = areaRect.top - wrapRect.top
    const originLeft = areaRect.left - wrapRect.left
    const padLeft = parseFloat(window.getComputedStyle(textarea).paddingLeft) || 0

    let top = originTop + caret.top - textarea.scrollTop
    let left = originLeft + caret.left + 6
    if (left + HINT_WIDTH_ESTIMATE_PX > originLeft + textarea.clientWidth) {
      top += caret.height
      left = originLeft + padLeft
    }

    const visibleTop = originTop
    const visibleBottom = originTop + textarea.clientHeight - caret.height
    if (top < visibleTop || top > visibleBottom) return null
    return { top, left }
  }, [])

  // Re-armed on every sign of activity (typing, caret moves, clicks, focus). The hint is
  // only offered when there is something to refine - FixTaskModal does nothing useful with
  // an empty description - and never while the modal itself is open.
  const armIdleHint = useCallback(() => {
    clearIdleTimer()
    setHint(null)
    if (isModalOpen) return
    idleTimerRef.current = setTimeout(() => {
      const textarea = descriptionRef.current
      if (!textarea || document.activeElement !== textarea) return
      if (!textarea.value.trim()) return
      setHint(computeHintPosition())
    }, IDLE_HINT_DELAY_MS)
  }, [isModalOpen, computeHintPosition])

  const dismissHint = () => {
    clearIdleTimer()
    setHint(null)
  }

  useEffect(() => clearIdleTimer, [])

  // Opening the refine modal hides any pending hint. Written against the ref directly so
  // the effect depends on nothing but the modal state.
  useEffect(() => {
    if (!isModalOpen) return
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
    setHint(null)
  }, [isModalOpen])

  // Tab only means "AI support" while the hint is actually showing. The rest of the time
  // it keeps its normal job of moving focus, so keyboard users can still tab through the
  // form - the shortcut is offered, never imposed.
  const handleDescriptionKeyDown = (e) => {
    if (hint && e.key === 'Tab' && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      e.preventDefault()
      dismissHint()
      handleAISuggestion()
      return
    }
    if (hint && e.key === 'Escape') {
      e.preventDefault()
      dismissHint()
      return
    }
    // Any other key is activity: hide now, and onKeyUp re-arms once the key is released.
    if (hint) setHint(null)
  }

  const needsFunctionName = typeMeta?.kind === 'code'

  return (
    <>
      <div className="cq-card__head">
        <div>
          <h2>Problem Details</h2>
          <p>What students see when they open the exercise.</p>
        </div>
      </div>

      <div className="cq-field">
        <div className="cq-field__label">
          <span>
            Problem name<span className="cq-required">*</span>
          </span>
        </div>
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder="Enter the problem name"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>

      <div className="cq-field">
        <div className="cq-field__label">
          <span>
            Problem description<span className="cq-required">*</span>
          </span>
          <Button
            size="small"
            startIcon={<AutoFixHighIcon fontSize="small" />}
            onClick={handleAISuggestion}
            disabled={!task.trim()}>
            Refine with AI
          </Button>
        </div>
        <div className="cq-ai-hint-host" ref={fieldWrapRef}>
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder="Describe the problem"
            value={task}
            inputRef={descriptionRef}
            // onScroll lives on the textarea itself: React 17+ no longer bubbles scroll
            // events, so a handler on the TextField root would never fire.
            inputProps={{ 'aria-describedby': 'cq-ai-hint-sr', onScroll: dismissHint }}
            onChange={(e) => setTask(e.target.value)}
            onKeyDown={handleDescriptionKeyDown}
            onKeyUp={armIdleHint}
            onClick={armIdleHint}
            onFocus={armIdleHint}
            onBlur={dismissHint}
          />
          {hint && (
            <span
              className="cq-ai-hint"
              role="status"
              style={{ top: hint.top, left: hint.left }}>
              Press <kbd>Tab</kbd> for AI support
            </span>
          )}
          <span id="cq-ai-hint-sr" className="cq-visually-hidden">
            When the cursor rests for a moment, press Tab to refine this description with
            AI.
          </span>
        </div>
      </div>

      {needsFunctionName && (
        <Alert severity="info">
          Name the function students must implement, explicitly and only once. The test
          harness is generated by asking the model for that name and then calling it
          literally, so an ambiguous description produces tests that raise{' '}
          <code>NameError</code> for every student.
        </Alert>
      )}

      {isModalOpen && (
        <FixTaskModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          taskContent={generateDescription}
          setTaskContent={setGenerateDescription}
          title="AI-Suggested Problem Description"
        />
      )}
    </>
  )
}

export default DetailsStep
